/**
 * Macro #20 — global unread-message notifications for the top bar.
 *
 * Polls `GET /api/v1/moderation/notifications` (SIWE-session authoritative) and
 * exposes two luminous-badge signals:
 *   - `ownerUnread` — an administrator replied on a case the wallet currently
 *     owns and the owner hasn't opened the thread yet.
 *   - `adminUnread` — (admin wallets only) an owner is waiting with a message the
 *     admin hasn't opened yet.
 *
 * Fail-closed: no session / any error → both false. Re-checks on mount, wallet
 * change, tab focus/visibility, hash/history navigation, a bounded safety poll,
 * and on the in-app `grid:moderation-read` event (fired the instant a thread is
 * opened) so the glow clears immediately without waiting for the next poll.
 */
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { moderationApi } from "../lib/moderation-api";

const POLL_MS = 20000;
export const MODERATION_READ_EVENT = "grid:moderation-read";

/** Fire after a support thread is opened so the top-bar glow refreshes at once. */
export function notifyModerationRead(): void {
  window.dispatchEvent(new Event(MODERATION_READ_EVENT));
}

export function useModerationNotifications(): {
  ownerUnread: boolean;
  adminUnread: boolean;
  ownerUnreadTargets: Set<string>;
} {
  const { address } = useAccount();
  const [ownerUnread, setOwnerUnread] = useState(false);
  const [adminUnread, setAdminUnread] = useState(false);
  const [ownerUnreadTargets, setOwnerUnreadTargets] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const controllers: AbortController[] = [];

    const run = async () => {
      const controller = new AbortController();
      controllers.push(controller);
      try {
        const n = await moderationApi.notifications(controller.signal);
        if (cancelled || controller.signal.aborted) return;
        setOwnerUnread(!!n.owner_unread);
        setAdminUnread(!!n.admin_unread);
        setOwnerUnreadTargets(
          new Set((n.owner_unread_targets ?? []).map((t) => `${t.target_type}:${t.target_id}`)),
        );
      } catch {
        if (cancelled || controller.signal.aborted) return;
        setOwnerUnread(false); // fail-closed
        setAdminUnread(false);
        setOwnerUnreadTargets(new Set());
      }
    };

    void run();
    const bg = () => { if (!cancelled) void run(); };
    const onVisible = () => { if (!cancelled && document.visibilityState === "visible") void run(); };
    window.addEventListener("focus", bg);
    window.addEventListener("pageshow", bg);
    window.addEventListener("hashchange", bg);
    window.addEventListener("popstate", bg);
    window.addEventListener(MODERATION_READ_EVENT, bg);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (!cancelled && document.visibilityState !== "hidden") void run();
    }, POLL_MS);

    return () => {
      cancelled = true;
      controllers.forEach((c) => c.abort());
      window.removeEventListener("focus", bg);
      window.removeEventListener("pageshow", bg);
      window.removeEventListener("hashchange", bg);
      window.removeEventListener("popstate", bg);
      window.removeEventListener(MODERATION_READ_EVENT, bg);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [address]);

  return { ownerUnread, adminUnread, ownerUnreadTargets };
}
